import React, { useState, useEffect } from 'react';

interface Allergy {
  id: string;
  name: string;
  isActive: boolean;
  createdAt: string;
}

const AllergyManager: React.FC = () => {
  const [allergies, setAllergies] = useState<Allergy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAllergy, setEditingAllergy] = useState<Allergy | null>(null);
  const [formName, setFormName] = useState('');

  useEffect(() => {
    loadAllergies();
  }, []);

  const loadAllergies = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/v1/allergies');
      const data = await response.json();
      if (data.success) {
        setAllergies(data.allergies);
      }
    } catch (error) {
      console.error('Failed to load allergies:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const url = editingAllergy
        ? `/api/v1/allergies/${editingAllergy.id}`
        : '/api/v1/allergies';

      const response = await fetch(url, {
        method: editingAllergy ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formName })
      });

      const data = await response.json();
      if (data.success) {
        await loadAllergies();
        setFormName('');
        setShowForm(false);
        setEditingAllergy(null);
      } else {
        alert(data.error || 'Failed to save allergy');
      }
    } catch (error) {
      console.error('Failed to save allergy:', error);
      alert('Failed to save allergy');
    }
  };

  const handleEdit = (allergy: Allergy) => {
    setEditingAllergy(allergy);
    setFormName(allergy.name);
    setShowForm(true);
  };

  const handleDelete = async (allergy: Allergy) => {
    if (window.confirm(`האם למחוק את האלרגיה "${allergy.name}"?`)) {
      try {
        const response = await fetch(`/api/v1/allergies/${allergy.id}`, {
          method: 'DELETE'
        });
        const data = await response.json();
        if (data.success) {
          await loadAllergies();
        } else {
          alert(data.error || 'Failed to delete allergy');
        }
      } catch (error) {
        console.error('Failed to delete allergy:', error);
        alert('Failed to delete allergy');
      }
    }
  };

  const toggleActive = async (allergy: Allergy) => {
    try {
      const response = await fetch(`/api/v1/allergies/${allergy.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !allergy.isActive })
      });
      const data = await response.json();
      if (data.success) {
        await loadAllergies();
      }
    } catch (error) {
      console.error('Failed to toggle allergy:', error);
    }
  };

  // Render function (not component) to prevent focus loss on parent re-render
  const renderAllergyForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {editingAllergy ? 'ערוך אלרגיה' : 'הוסף אלרגיה חדשה'}
        </h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              שם האלרגיה *
            </label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              placeholder="למשל: גלוטן, בוטנים, חלב..."
              autoFocus
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              {editingAllergy ? 'עדכן' : 'הוסף'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingAllergy(null);
                setFormName('');
              }}
              className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
            >
              ביטול
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">ניהול אלרגיות</h2>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            נהל את רשימת האלרגיות לאירועים
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          הוסף אלרגיה
        </button>
      </div>

      {/* Allergies List */}
      {allergies.length === 0 ? (
        <div className="text-center py-12">
          <div className="bg-gray-100 dark:bg-gray-800 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            אין אלרגיות ברשימה
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            הוסף אלרגיות נפוצות כמו גלוטן, בוטנים, חלב וכדומה
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            הוסף אלרגיה ראשונה
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  שם האלרגיה
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  סטטוס
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  פעולות
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
              {allergies.map((allergy) => (
                <tr key={allergy.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900 dark:text-white">
                    {allergy.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <button
                      onClick={() => toggleActive(allergy)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        allergy.isActive
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300'
                      }`}
                    >
                      {allergy.isActive ? 'פעיל' : 'לא פעיל'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex justify-center gap-2">
                      <button
                        onClick={() => handleEdit(allergy)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(allergy)}
                        className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Modal */}
      {showForm && renderAllergyForm()}
    </div>
  );
};

export default AllergyManager;
